import React, { useState } from 'react'
import { format, formatISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { parsePhoneNumber } from 'libphonenumber-js'
import { Typography } from '@telavita-core/react-design-kit'

import ModalGeneric from 'containers/ModalManager/Modals/ModalGeneric'
import { InitialContent, RescheduleContent, SuccessContent } from './components'

import { Reservation } from 'models/Reservation'

import * as S from './styles'

interface RescheduleNonRecurrentReservationModalProps {
  handleClose: () => void
  onRefresh: () => void
  reservation: Reservation
}

enum ModalStatuses {
  INITIAL = 'INITIAL',
  RESCHEDULE = 'RESCHEDULE',
  SUCCESS = 'SUCCESS'
}

function formatDate(date: Date): {weekday: string, time: string, formattedDate: string} {
  const hour = format(date, 'HH')
  const minute = format(date, 'mm')
  const time = `${hour}h${minute}`
  const weekday = format(date, 'EEEE', { locale: ptBR })
  const formattedDate = format(date, 'dd/MM/yyyy')

  return {weekday, time, formattedDate}
}

function formatPhone(phone: string) {
  return isNaN(Number(phone)) 
    ? '' 
    : parsePhoneNumber(`+${phone}`).formatNational()
}

function getModalTitleByModalStatus(
  modalStatus: ModalStatuses,
  formattedDate: string
) {
  return modalStatus === ModalStatuses.SUCCESS
    ? ''
    : <>Remarcar reserva de <S.DangerText>{formattedDate}</S.DangerText></>
}

function getModalSubtitleByModalStatus(
  patientFullName: string, 
  weekday: string,
  time: string,
  modalStatus: ModalStatuses
) {
  const modalSubtitles = {
    INITIAL: <>Paciente: <strong>{patientFullName}</strong></>,
    SUCCESS: '',
    RESCHEDULE: (
      <>
        <Typography variant='content2' inlineStyles={{ color: '#898989' }} center>
          <b><span style={{ textTransform: 'capitalize'} }>{weekday}</span>, às {time}</b>
        </Typography>
        <Typography variant='content2' inlineStyles={{ color: '#898989' }} center>
          com Paciente <strong>{patientFullName}</strong>
        </Typography>
      </>
    ),
  }

  return modalSubtitles[modalStatus]
}

export const RescheduleNonRecurrentReservationModal: React.FC<RescheduleNonRecurrentReservationModalProps> = ({
  handleClose,
  onRefresh,
  reservation
}) => {
  const [modalStatus, setModalStatus] = useState<ModalStatuses>(ModalStatuses.INITIAL)
  const [newReservationDate, setNewReservationDate] = useState('')
  
  const { startDate, patientFullName, patientCellphone } = reservation
  
  const { weekday, time, formattedDate } = formatDate(startDate)
  const patientPhone = formatPhone(patientCellphone)
  const modalTitle = getModalTitleByModalStatus(modalStatus, formattedDate)
  const modalSubtitle = getModalSubtitleByModalStatus(patientFullName, weekday, time, modalStatus)

  function handleContinueReschedule() {
    setModalStatus(ModalStatuses.RESCHEDULE)
  }

  function handleCloseModal() {
    if (modalStatus === ModalStatuses.SUCCESS) {
      onRefresh()
    }
    
    setModalStatus(ModalStatuses.INITIAL)
    handleClose()
  }

  function handleSuccess(newReservationDate: string) {
    setModalStatus(ModalStatuses.SUCCESS)
    setNewReservationDate(newReservationDate)
  }

  return (
    <ModalGeneric modalTitle={modalTitle} modalSubtitle={modalSubtitle} onCloseModal={handleCloseModal}>
      {modalStatus === ModalStatuses.INITIAL && (
        <InitialContent patientPhone={patientPhone} handleContinueReschedule={handleContinueReschedule}/>
      )}

      {modalStatus === ModalStatuses.RESCHEDULE && (
        <RescheduleContent reservation={reservation} handleSuccess={handleSuccess}/>
      )}

      {modalStatus === ModalStatuses.SUCCESS && (
        <SuccessContent 
          handleClose={handleCloseModal} 
          patientFullName={patientFullName}
          oldDate={formatISO(startDate)}
          newDate={newReservationDate}
        />
      )}
    </ModalGeneric>
  )
}